import React, { useEffect, useMemo, useRef, useState } from "react";

import HttpManager from "../../../../services/HttpManager";
import {
	AutoRegla, Destino, HeaderTipo, Lto, Plantilla, PlantillaBoton, PlantillasCatalogos, RevisionIA, VisTipo, Visibilidad
} from "../../../../services/v2/plantillas.service";
import VistaPrevia from "./vistaPrevia";
import AutoRespuestas from "./autoRespuestas";

const CRITERIOS: Record<string, string> = {
	calidez: "Calidez", personal: "Personal", claridad: "Claridad", accion: "Llamada a la acción", ortografia: "Ortografía", meta: "Riesgo con Meta",
};

const errorTexto = (e: any): string => {
	const d = e?.response?.data;
	return d?.message || d?.errors?.error?.[0] || e?.message || "Algo falló. Intenta otra vez.";
};

const HEADERS: { k: HeaderTipo; t: string }[] = [
	{ k: "none", t: "Sin encabezado" }, { k: "text", t: "Título" }, { k: "image", t: "Foto" }, { k: "video", t: "Video" }, { k: "document", t: "PDF" },
];
const BOTON_TIPOS: { k: PlantillaBoton["tipo"]; t: string }[] = [
	{ k: "quick", t: "Respuesta rápida" }, { k: "url", t: "Abrir liga" }, { k: "phone", t: "Llamar" }, { k: "copy", t: "Copiar código" },
];
const VIS: { k: VisTipo; t: string; super?: boolean }[] = [
	{ k: "yo", t: "Solo yo" }, { k: "territorio", t: "Mi territorio" }, { k: "equipo", t: "Mi equipo", super: true },
	{ k: "asesoras", t: "Asesoras específicas", super: true }, { k: "todos", t: "Toda la escuela", super: true },
];
const EMOJIS = ["😊", "🎉", "🎁", "🔥", "✨", "📲", "👉", "✅", "📅", "⏰", "🎓", "💈", "🍰", "🍳", "💅", "🇭🇳", "🇲🇽", "🙌"];
const ESTADO_META: Record<string, string> = {
	PENDIENTE: "Sin mandar", PENDING: "En revisión de Meta", APPROVED: "Aprobada por Meta", REJECTED: "Rechazada por Meta",
	PAUSED: "Pausada por Meta", DISABLED: "Desactivada por Meta", ERROR: "No se pudo crear", IN_APPEAL: "En apelación",
};

interface Props {
	campusId: number;
	cat: PlantillasCatalogos;
	plantilla: Plantilla | null;
	/** Departamento con el que arranca una nueva (el que está elegido en la lista). */
	deptNueva?: string;
	onClose: () => void;
	onCambio: (p: Plantilla) => void;
}

/**
 * Crear, revisar y aprobar una plantilla (Esteban, 2026-10-05): a la izquierda lo que se escribe, a la derecha cómo le
 * llega al cliente. La asesora la manda a aprobación; la supervisora la aprueba y WAPP la crea en Meta.
 */
const PlantillaEditor: React.FC<Props> = ({ campusId, cat, plantilla, deptNueva, onClose, onCambio }) => {
	const http = HttpManager.getInstance();
	const p0 = plantilla;
	const deptInicial = p0?.dept || deptNueva || cat.departamentos[0]?.clave || "ventas";
	const [nombre, setNombre] = useState<string>(p0?.nombre || "");
	const [dept, setDept] = useState<string>(deptInicial);
	const [territorio, setTerritorio] = useState<string>(p0?.territorio || (cat.alcance[deptInicial]?.territorios.find((t) => t !== "*") || cat.territorios[0]?.clave || ""));
	const [categoria, setCategoria] = useState<"MARKETING" | "UTILITY">(p0?.categoria || "UTILITY");   // Aviso primero: cuesta unas 6 veces menos (Esteban, 2026-10-14)
	const [headerTipo, setHeaderTipo] = useState<HeaderTipo>(p0?.header_tipo || "none");
	const [headerTexto, setHeaderTexto] = useState<string>(p0?.header_texto || "");
	const [archivo, setArchivo] = useState<File | null>(null);
	const [mediaUrl, setMediaUrl] = useState<string | null>(null);
	const [cuerpo, setCuerpo] = useState<string>(p0?.cuerpo || "");
	const [pie, setPie] = useState<string>(p0?.pie || "");
	const [botones, setBotones] = useState<PlantillaBoton[]>(p0?.botones || []);
	const [ejemplos, setEjemplos] = useState<Record<string, string>>(p0?.ejemplos || {});
	const [vis, setVis] = useState<Visibilidad>(p0?.visibilidad || { tipo: "territorio" });
	const [personas, setPersonas] = useState<{ id: number; nombre: string }[]>([]);
	const [revision, setRevision] = useState<RevisionIA | null>(p0?.revision || null);
	const [reglas, setReglas] = useState<string[]>([]);
	const [sugerencias, setSugerencias] = useState<string[]>([]);
	const [revisando, setRevisando] = useState<boolean>(false);
	const [ocupado, setOcupado] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [destinos, setDestinos] = useState<Destino[] | null>(null);
	const [cuentas, setCuentas] = useState<string[]>([]);
	const [rechazando, setRechazando] = useState<boolean>(false);
	const [motivo, setMotivo] = useState<string>("");
	const [verEmojis, setVerEmojis] = useState<boolean>(false);
	const [lto, setLto] = useState<Lto>(p0?.lto || { activa: false, titulo: "", horas: 48 });
	const [auto, setAuto] = useState<AutoRegla[]>(p0?.auto || []);
	const [autoArchivos, setAutoArchivos] = useState<Record<string, File>>({});
	// Lo que se calificó: si cambia el texto, la calificación vieja ya no vale.
	const firmaTexto = (headerTipo === "text" ? headerTexto : "") + "|" + cuerpo + "|" + pie + "|" + lto.activa + lto.titulo;
	const [firmaRevisada, setFirmaRevisada] = useState<string | null>(p0?.calidad?.vigente ? firmaTexto : null);
	const caja = useRef<HTMLTextAreaElement>(null);

	const editable = !p0 || p0.puede.editar;
	const supervisa = useMemo(() => {
		const a = cat.alcance[dept];
		return !!a && (a.supervisa.includes("*") || a.supervisa.includes(territorio));
	}, [cat, dept, territorio]);
	const L = cat.limites;
	const varsCat = useMemo(() => Object.fromEntries(cat.variables.map((v) => [v.clave, v])), [cat]);
	const usadas = useMemo(() => {
		const r = /\{\{\s*([a-z_]+)\s*\}\}/g; const out: string[] = []; let m;
		const t = (headerTipo === "text" ? headerTexto + " " : "") + cuerpo;
		while ((m = r.exec(t))) if (!out.includes(m[1])) out.push(m[1]);
		return out;
	}, [cuerpo, headerTexto, headerTipo]);
	const valoresVista = useMemo(() => Object.fromEntries(cat.variables.map((v) => [v.clave, ejemplos[v.clave] || v.ejemplo])), [cat, ejemplos]);
	const largoMeta = (t: string) => t.replace(/\{\{\s*[a-z_]+\s*\}\}/g, "{{1}}").length;

	// Vista previa del archivo: el nuevo (local) o el guardado.
	useEffect(() => {
		if (archivo) {
			const u = URL.createObjectURL(archivo);
			setMediaUrl(u);
			return () => URL.revokeObjectURL(u);
		}
		if (p0?.tiene_media && p0.header_tipo === headerTipo) {
			let u: string | null = null;
			http.plantillasService.archivo(campusId, p0.id).then((r) => { u = URL.createObjectURL(r.data); setMediaUrl(u); }).catch(() => setMediaUrl(null));
			return () => { if (u) URL.revokeObjectURL(u); };
		}
		setMediaUrl(null);
	}, [archivo, headerTipo, p0?.id]);

	useEffect(() => {
		if (vis.tipo !== "asesoras") return;
		http.plantillasService.personas(campusId, dept, territorio || null).then((r) => setPersonas(r.data)).catch(() => setPersonas([]));
	}, [vis.tipo, dept, territorio]);

	// En qué números se crea (todos marcados; la supervisora puede quitar). También para la asesora: si el departamento no
	// tiene número en el territorio, se le dice antes de mandarla a aprobar (Torreón, 2026-10-13: nadie sabía por qué no se podía).
	useEffect(() => {
		if (!territorio || !(editable || p0?.puede.aprobar)) { setDestinos(null); return; }
		http.plantillasService.destinos(campusId, dept, territorio || null).then((r) => { setDestinos(r.data); setCuentas(r.data.map((d) => d.waba_id)); })
			.catch(() => setDestinos([]));
	}, [dept, territorio, editable, p0?.id]);
	const sinNumeros = destinos !== null && destinos.length === 0;
	const deptNombre = cat.departamentos.find((d) => d.clave === dept)?.nombre || dept;
	const terrNombre = cat.territorios.find((t) => t.clave === territorio)?.nombre || territorio;

	const insertar = (antes: string, despues = "") => {
		const t = caja.current;
		if (!t) { setCuerpo(cuerpo + antes + despues); return; }
		const a = t.selectionStart, b = t.selectionEnd;
		const nuevo = cuerpo.slice(0, a) + antes + cuerpo.slice(a, b) + despues + cuerpo.slice(b);
		setCuerpo(nuevo);
		requestAnimationFrame(() => { t.focus(); const pos = b + antes.length + despues.length; t.setSelectionRange(despues ? a + antes.length : pos, despues ? b + antes.length : pos); });
	};

	const datos = () => ({
		nombre, dept, territorio: territorio || null, categoria, header_tipo: headerTipo, header_texto: headerTexto, cuerpo, pie: lto.activa ? "" : pie, botones,
		ejemplos, visibilidad: vis, revision, lto: lto.activa ? lto : null, auto,
	});
	// Calificación con IA: apagada por ahora (Esteban, 2026-10-05); lo que manda es la aprobación de la supervisora.
	const puntos = cat.calificacion && firmaRevisada === firmaTexto ? (revision?.puntos ?? p0?.calidad?.puntos ?? null) : null;
	const bajaCalidad = puntos !== null && puntos < cat.calidad_minima;
	const criterios = cat.calificacion && firmaRevisada === firmaTexto ? (revision?.criterios || p0?.calidad?.criterios || {}) : {};
	const resumen = firmaRevisada === firmaTexto ? (revision?.resumen || p0?.calidad?.resumen || "") : "";
	const mejorada = cat.calificacion && firmaRevisada === firmaTexto ? (revision?.mejorada || p0?.calidad?.mejorada || null) : null;
	// Cuenta regresiva: Meta pide copiar código (opcional) primero y una liga; sin pie ni respuestas rápidas.
	const activarLto = (activa: boolean) => {
		setLto({ ...lto, activa });
		if (activa) {
			setCategoria("MARKETING");
			const liga = botones.find((b) => b.tipo === "url") || { tipo: "url" as const, texto: "Ver promoción", url: "https://" };
			const codigo = botones.find((b) => b.tipo === "copy");
			setBotones([...(codigo ? [codigo] : []), liga]);
		}
	};

	const revisar = async () => {
		setRevisando(true); setError(null);
		try {
			const r = await http.plantillasService.revisar(campusId, datos());
			setReglas(r.data.reglas || []);
			setSugerencias(r.data.sugerencias || []);
			setRevision(r.data.ia);
			setFirmaRevisada(firmaTexto);
		} catch (e) { setError(errorTexto(e)); }
		finally { setRevisando(false); }
	};

	const aplicarCorreccion = () => {
		if (!revision) return;
		if (revision.texto) setCuerpo(revision.texto);
		if (revision.titulo && headerTipo === "text") setHeaderTexto(revision.titulo);
		if (revision.pie && pie) setPie(revision.pie);
		setRevision({ ...revision, ok: true, correcciones: [] });
	};

	const guardar = async (accion: "borrador" | "sugerir" | "aprobar") => {
		setOcupado(true); setError(null);
		try {
			let r = await http.plantillasService.guardar(campusId, p0?.id || null, datos(), archivo, accion === "aprobar" ? "sugerir" : accion, autoArchivos);
			if (accion === "aprobar") r = await http.plantillasService.aprobar(campusId, r.data.id, cuentas);
			onCambio(r.data);
			if (accion !== "borrador") onClose();
		} catch (e) { setError(errorTexto(e)); }
		finally { setOcupado(false); }
	};

	const accion = async (fn: () => Promise<{ data: Plantilla }>, cerrar = true) => {
		setOcupado(true); setError(null);
		try { const r = await fn(); onCambio(r.data); if (cerrar) onClose(); }
		catch (e) { setError(errorTexto(e)); }
		finally { setOcupado(false); }
	};

	const setBoton = (i: number, cambios: Partial<PlantillaBoton>) => setBotones(botones.map((b, j) => (j === i ? { ...b, ...cambios } : b)));
	const media = L.media[headerTipo];
	const elegirArchivo = (f: File | null) => {
		setError(null);
		if (!f || !media) { setArchivo(null); return; }
		if (!media.mimes.includes(f.type)) { setError("Meta solo acepta " + media.nombre + "."); return; }
		if (f.size > media.max) { setError("Meta solo acepta " + media.nombre + ". Este pesa " + (f.size / 1048576).toFixed(1) + " MB."); return; }
		setArchivo(f);
	};

	const contador = (n: number, max: number) => <span className={"pl-cuenta" + (n > max ? " pasa" : "")}>{n}/{max}</span>;

	return (
		<div className="pl-modal" role="dialog" aria-modal="true">
			<div className="pl-modal-cab">
				<h2>{p0 ? (editable ? "Editar plantilla" : p0.nombre) : "Nueva plantilla"}</h2>
				{p0 ? <span className={"pl-estado e-" + p0.estado}>{p0.estado_nombre}</span> : null}
				<button className="pl-x" onClick={onClose} aria-label="Cerrar" disabled={ocupado}>×</button>
			</div>
			<div className="pl-modal-cuerpo">
				<div className="pl-form">
					{p0?.estado === "rechazada" && p0.motivo ? <div className="pl-aviso oro"><b>Por corregir:</b> {p0.motivo}</div> : null}
					{error ? <div className="pl-aviso oro">{error}</div> : null}

					<div className="pl-fila">
						<label className="pl-campo crece"><span>Nombre</span>
							<input value={nombre} onChange={(e) => setNombre(e.target.value)} disabled={!editable} maxLength={120} placeholder="Ej. Boomerang con horarios" />
						</label>
						<label className="pl-campo"><span>Departamento</span>
							<select value={dept} onChange={(e) => setDept(e.target.value)} disabled={!editable || !!p0}>
								{cat.departamentos.map((d) => <option key={d.clave} value={d.clave}>{d.nombre}</option>)}
							</select>
						</label>
						<label className="pl-campo"><span>Territorio</span>
							<select value={territorio} onChange={(e) => setTerritorio(e.target.value)} disabled={!editable || !!p0?.meta_name}>
								{cat.territorios.map((t) => <option key={t.clave} value={t.clave}>{t.nombre}</option>)}
							</select>
						</label>
						<label className="pl-campo"><span>Tipo</span>
							<select value={categoria} onChange={(e) => setCategoria(e.target.value as any)} disabled={!editable}>
								<option value="UTILITY">Aviso o seguimiento (más barato)</option>
								<option value="MARKETING">Promoción</option>
							</select>
							<small className="pl-costo">{categoria === "UTILITY" ? "Cerca de 1 centavo por envío, y gratis si el cliente escribió en las últimas 24 h." : "De 4 a 7 centavos por envío, siempre. Úsala para promos de verdad."}</small>
						</label>
					</div>

					<div className="pl-bloque">
						<div className="pl-bloque-tit">Encabezado</div>
						<div className="pl-segmento" role="radiogroup">
							{HEADERS.map((h) => (
								<button key={h.k} role="radio" aria-checked={headerTipo === h.k} className={headerTipo === h.k ? "on" : ""} disabled={!editable}
									onClick={() => { setHeaderTipo(h.k); setArchivo(null); }}>{h.t}</button>
							))}
						</div>
						{headerTipo === "text" ? (
							<label className="pl-campo"><span>Título {contador(largoMeta(headerTexto), L.titulo)}</span>
								<input value={headerTexto} onChange={(e) => setHeaderTexto(e.target.value)} disabled={!editable} placeholder="Ej. ¡Promo Boomerang! 🪃" />
							</label>
						) : null}
						{media ? (
							<label className={"pl-subir" + (editable ? "" : " deshab")}>
								<input type="file" hidden accept={media.mimes.join(",")} disabled={!editable} onChange={(e) => elegirArchivo(e.target.files?.[0] || null)} />
								<b>{archivo ? archivo.name : p0?.tiene_media && p0.header_tipo === headerTipo ? (p0.header_media_nombre || "Archivo guardado") : "Subir " + media.nombre}</b>
								<small>{archivo || (p0?.tiene_media && p0.header_tipo === headerTipo) ? "Toca para cambiarlo" : "Se puede cambiar en cada envío"}</small>
							</label>
						) : null}
					</div>

					{editable && !p0?.meta_name && (cat.ejemplos || []).length > 0 ? (
						<details className="pl-bloque pl-modelos">
							<summary className="pl-bloque-tit"><span>Ejemplos que están vendiendo</span></summary>
							<div className="pl-modelos-lista">
							<p className="pl-modelos-nota">Las que más contestan en Honduras y México. Toca “Usar como base” y cámbiala a tu estilo.</p>
							{(cat.ejemplos || []).map((e, i) => (
								<div key={i} className="pl-modelo">
									<div className="pl-modelo-cab"><b>{e.titulo}</b><small>{e.dato}</small></div>
									<p>{e.cuerpo}</p>
									{e.botones.length ? <div className="pl-modelo-botones">{e.botones.map((b, j) => <span key={j}>{b.texto}</span>)}</div> : null}
									<button className="pl-btn chico" onClick={() => { setCuerpo(e.cuerpo); setCategoria(e.categoria); setBotones(e.botones); setFirmaRevisada(null); }}>Usar como base</button>
								</div>
							))}
							</div>
						</details>
					) : null}

					<div className="pl-bloque">
						<div className="pl-bloque-tit">Mensaje {contador(largoMeta(cuerpo), L.cuerpo)}</div>
						{editable ? (
							<div className="pl-herramientas">
								<button title="Negrita" onClick={() => insertar("*", "*")}><b>B</b></button>
								<button title="Cursiva" onClick={() => insertar("_", "_")}><i>I</i></button>
								<button title="Tachado" onClick={() => insertar("~", "~")}><s>S</s></button>
								<span className="pl-sep" />
								<div className="pl-emojis">
									<button title="Emoji" onClick={() => setVerEmojis(!verEmojis)}>😊</button>
									{verEmojis ? <div className="pl-emojis-caja">{EMOJIS.map((e) => <button key={e} onClick={() => { insertar(e); setVerEmojis(false); }}>{e}</button>)}</div> : null}
								</div>
								<span className="pl-sep" />
								{cat.variables.filter((v) => !v.depts || v.depts.includes(dept)).map((v) => (
									<button key={v.clave} className="pl-chip" title={"Insertar " + v.etiqueta} onClick={() => insertar("{{" + v.clave + "}}")}>+ {v.etiqueta}</button>
								))}
							</div>
						) : null}
						<textarea ref={caja} className="pl-texto" rows={9} value={cuerpo} onChange={(e) => setCuerpo(e.target.value)} disabled={!editable}
							placeholder={"Hola {{nombre}}, le saluda {{asesora}} de Washington Academy 😊\n\n…\n\n¿Le interesa?"} />
					</div>

					{!lto.activa ? (
						<label className="pl-campo"><span>Pie (opcional) {contador(pie.length, L.pie)}</span>
							<input value={pie} onChange={(e) => setPie(e.target.value)} disabled={!editable} placeholder="Ej. Washington Academy" />
						</label>
					) : null}

					{categoria === "MARKETING" ? (
						<div className="pl-bloque">
							<div className="pl-bloque-tit">
								<span>Cuenta regresiva <small>opcional</small></span>
								<label className="pl-switch"><input type="checkbox" checked={lto.activa} disabled={!editable} onChange={(e) => activarLto(e.target.checked)} /><span>{lto.activa ? "Activa" : "Apagada"}</span></label>
							</div>
							{lto.activa ? (
								<div className="pl-fila">
									<label className="pl-campo crece"><span>Título de la oferta {contador(lto.titulo.length, cat.lto.titulo)}</span>
										<input value={lto.titulo} maxLength={40} disabled={!editable} placeholder="Ej. Promo Boomerang" onChange={(e) => setLto({ ...lto, titulo: e.target.value })} />
									</label>
									<label className="pl-campo"><span>Vence en (horas)</span>
										<input type="number" min={1} max={720} value={lto.horas} disabled={!editable} onChange={(e) => setLto({ ...lto, horas: Number(e.target.value) || 48 })} />
									</label>
								</div>
							) : null}
							{lto.activa ? <div className="pl-tenue">El cliente ve un reloj con lo que falta. Al mandarla se puede cambiar la fecha. Lleva un botón de liga y, si quieres, uno con el código del cupón.</div> : null}
						</div>
					) : null}

					<div className="pl-bloque">
						<div className="pl-bloque-tit">Botones <small>{botones.length}/{L.botones}</small></div>
						{botones.map((b, i) => (
							<div key={i} className="pl-boton-fila">
								<select value={b.tipo} onChange={(e) => setBoton(i, { tipo: e.target.value as any })} disabled={!editable}>
									{BOTON_TIPOS.map((t) => <option key={t.k} value={t.k}>{t.t}</option>)}
								</select>
								{b.tipo !== "copy" ? <input value={b.texto || ""} maxLength={40} placeholder="Texto del botón" onChange={(e) => setBoton(i, { texto: e.target.value })} disabled={!editable} /> : null}
								{b.tipo === "url" ? <input value={b.url || ""} placeholder="https://…" onChange={(e) => setBoton(i, { url: e.target.value })} disabled={!editable} /> : null}
								{b.tipo === "phone" ? <input value={b.telefono || ""} placeholder="+50495866310" onChange={(e) => setBoton(i, { telefono: e.target.value })} disabled={!editable} /> : null}
								{b.tipo === "copy" ? <input value={b.ejemplo || ""} placeholder="Código, ej. PROMO50" onChange={(e) => setBoton(i, { ejemplo: e.target.value })} disabled={!editable} /> : null}
								{b.tipo !== "copy" && (b.texto || "").length > L.boton_texto ? <span className="pl-cuenta pasa">{(b.texto || "").length}/{L.boton_texto}</span> : null}
								{editable ? <button className="pl-quitar" onClick={() => setBotones(botones.filter((_, j) => j !== i))} aria-label="Quitar botón">×</button> : null}
							</div>
						))}
						{editable && botones.length < L.botones && !lto.activa ? (
							<div className="pl-agregar">
								<button onClick={() => setBotones([...botones, { tipo: "quick", texto: "" }])}>+ Respuesta rápida</button>
								<button onClick={() => setBotones([...botones, { tipo: "url", texto: "", url: "https://" }])}>+ Liga</button>
								<button onClick={() => setBotones([...botones, { tipo: "phone", texto: "Llamar", telefono: "" }])}>+ Llamar</button>
								<button onClick={() => setBotones([...botones, { tipo: "copy", ejemplo: "" }])}>+ Copiar código</button>
							</div>
						) : null}
					</div>

					<div className="pl-bloque">
						<div className="pl-bloque-tit">Respuestas automáticas</div>
						<AutoRespuestas reglas={auto} setReglas={setAuto} botones={botones.filter((b) => b.tipo === "quick").map((b) => b.texto || "")}
							statuses={cat.statuses} archivos={autoArchivos} setArchivos={setAutoArchivos} editable={editable || !!p0?.puede.visibilidad} />
						{p0 && !editable && p0.puede.visibilidad ? (
							<button className="pl-btn chico" disabled={ocupado} onClick={() => accion(() => http.plantillasService.auto(campusId, p0.id, auto, autoArchivos), false)}>Guardar respuestas automáticas</button>
						) : null}
					</div>

					{usadas.length > 0 ? (
						<div className="pl-bloque">
							<div className="pl-bloque-tit">Ejemplos para Meta</div>
							<div className="pl-ejemplos">
								{usadas.map((k) => (
									<label key={k} className="pl-campo"><span>{varsCat[k]?.etiqueta || k}{varsCat[k]?.auto ? " · se llena solo" : ""}</span>
										<input value={ejemplos[k] ?? ""} placeholder={varsCat[k]?.ejemplo || ""} disabled={!editable} onChange={(e) => setEjemplos({ ...ejemplos, [k]: e.target.value })} />
									</label>
								))}
							</div>
						</div>
					) : null}

					<div className="pl-bloque">
						<div className="pl-bloque-tit">Quién la puede usar</div>
						<div className="pl-segmento">
							{VIS.filter((v) => supervisa || !v.super || v.k === vis.tipo).map((v) => (
								<button key={v.k} className={vis.tipo === v.k ? "on" : ""} disabled={!(editable || p0?.puede.visibilidad)}
									onClick={() => setVis({ tipo: v.k, equipo: v.k === "equipo" ? (vis.equipo || cat.equipo) : null, ids: v.k === "asesoras" ? (vis.ids || []) : [] })}>{v.t}</button>
							))}
						</div>
						{vis.tipo === "equipo" ? (
							<label className="pl-campo"><span>Equipo</span><input value={vis.equipo || ""} placeholder="Ej. Ateneas" onChange={(e) => setVis({ ...vis, equipo: e.target.value })} /></label>
						) : null}
						{vis.tipo === "asesoras" ? (
							<div className="pl-personas">
								{personas.map((x) => (
									<label key={x.id} className={(vis.ids || []).includes(x.id) ? "on" : ""}>
										<input type="checkbox" checked={(vis.ids || []).includes(x.id)}
											onChange={(e) => setVis({ ...vis, ids: e.target.checked ? [...(vis.ids || []), x.id] : (vis.ids || []).filter((y) => y !== x.id) })} />{x.nombre}
									</label>
								))}
								{!personas.length ? <span className="pl-tenue">Cargando…</span> : null}
							</div>
						) : null}
						{!supervisa && editable ? <div className="pl-tenue">Equipo, asesoras específicas o toda la escuela los escoge tu supervisora.</div> : null}
						{p0 && !editable && p0.puede.visibilidad ? (
							<button className="pl-btn chico" disabled={ocupado} onClick={() => accion(() => http.plantillasService.visibilidad(campusId, p0.id, vis), false)}>Guardar quién la usa</button>
						) : null}
					</div>

					{p0 && p0.numeros.length > 0 ? (
						<div className="pl-bloque">
							<div className="pl-bloque-tit">En Meta</div>
							{p0.numeros.map((n) => (
								<div key={n.waba_id} className={"pl-numero m-" + n.estado}>
									<span className="pl-numero-est">{ESTADO_META[n.estado] || n.estado}</span>
									<span>{n.numeros.join(", ") || "Cuenta " + n.waba_id}</span>
									{n.motivo ? <small>{n.motivo}</small> : null}
								</div>
							))}
						</div>
					) : null}

					{destinos && (editable || p0?.puede.aprobar) && (p0 ? p0.puede.aprobar : supervisa) ? (
						<div className="pl-bloque">
							<div className="pl-bloque-tit">Se crea en estos números</div>
							{destinos.map((d) => (
								<label key={d.waba_id} className="pl-destino">
									<input type="checkbox" checked={cuentas.includes(d.waba_id)} onChange={(e) => setCuentas(e.target.checked ? [...cuentas, d.waba_id] : cuentas.filter((x) => x !== d.waba_id))} />
									{d.numeros.join(", ")}
								</label>
							))}
						</div>
					) : null}
				</div>

				<div className="pl-lado">
					<VistaPrevia headerTipo={headerTipo} headerTexto={headerTexto} mediaUrl={mediaUrl} mediaNombre={archivo?.name || p0?.header_media_nombre}
						cuerpo={cuerpo} pie={lto.activa ? "" : pie} botones={botones} valores={valoresVista} resaltar lto={lto.activa ? lto : null} />
					<div className="pl-revision">
						<div className="pl-revision-cab">
							<b>Revisión</b>
							<button className="pl-btn chico" onClick={revisar} disabled={revisando || !cuerpo.trim()}>{revisando ? "Revisando…" : "Revisar ortografía y reglas"}</button>
						</div>
						{puntos !== null ? (
							<div className={"pl-calidad" + (bajaCalidad ? " baja" : "")}>
								<div className="pl-calidad-num"><b>{puntos}</b><span>/10</span></div>
								<div className="pl-calidad-txt">
									<b>{bajaCalidad ? "No se puede mandar todavía" : puntos >= 9 ? "Excelente" : "Buena para mandar"}</b>
									{resumen ? <span>{resumen}</span> : null}
								</div>
							</div>
						) : cat.calificacion && firmaRevisada && firmaRevisada !== firmaTexto ? <div className="pl-tenue">Cambiaste el texto: vuelve a revisar.</div> : null}
						{Object.keys(criterios).length > 0 ? (
							<div className="pl-criterios">
								{Object.entries(criterios).map(([k, v]) => (
									<div key={k} className="pl-criterio"><span>{CRITERIOS[k] || k}</span><i><em style={{ width: (Number(v) * 10) + "%" }} className={Number(v) < cat.calidad_minima ? "baja" : ""} /></i><b>{v}</b></div>
								))}
							</div>
						) : null}
						{mejorada && editable ? (
							<div className="pl-mejorada">
								<b>Versión mejorada</b>
								<p>{mejorada}</p>
								<button className="pl-btn chico pri" onClick={() => { setCuerpo(mejorada); setFirmaRevisada(null); }}>Usar esta versión</button>
							</div>
						) : null}
						{revision?.aviso_utility && editable && categoria === "MARKETING" && firmaRevisada === firmaTexto ? (
							<div className="pl-mejorada">
								<b>Como Aviso cuesta unas 6 veces menos</b>
								<p>{revision.aviso_utility}</p>
								<button className="pl-btn chico pri" onClick={() => { setCuerpo(revision.aviso_utility || ""); setCategoria("UTILITY"); setFirmaRevisada(null); }}>Usar como Aviso</button>
							</div>
						) : null}
						{reglas.length > 0 ? <ul className="pl-reglas">{reglas.map((r, i) => <li key={i}>{r}</li>)}</ul> : null}
						{sugerencias.length > 0 && firmaRevisada === firmaTexto ? <ul className="pl-sugerencias">{sugerencias.map((r, i) => <li key={i}>{r}</li>)}</ul> : null}
						{revision?.aviso ? <div className="pl-tenue">{revision.aviso}</div> : null}
						{revision && revision.ok === true && !reglas.length ? <div className="pl-ok">Sin errores de ortografía.</div> : null}
						{revision?.correcciones && revision.correcciones.length > 0 ? (
							<>
								<ul className="pl-correcciones">
									{revision.correcciones.map((c, i) => <li key={i}><s>{c.antes}</s> → <b>{c.despues}</b><small>{c.por_que}</small></li>)}
								</ul>
								{editable ? <button className="pl-btn" onClick={aplicarCorreccion}>Aplicar correcciones</button> : null}
							</>
						) : null}
						{revision?.avisos && revision.avisos.length > 0 ? <ul className="pl-avisos">{revision.avisos.map((a, i) => <li key={i}>{a}</li>)}</ul> : null}
					</div>
				</div>
			</div>

			{sinNumeros ? (
				<div className="pl-aviso oro">{deptNombre} todavía no tiene número de WhatsApp en {terrNombre}. Puedes guardarla como borrador, pero no se puede aprobar hasta que haya uno.</div>
			) : null}
			<div className="pl-modal-pie">
				{rechazando ? (
					<div className="pl-rechazo">
						<input autoFocus value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Qué hay que cambiar" />
						<button className="pl-btn" onClick={() => setRechazando(false)} disabled={ocupado}>Volver</button>
						<button className="pl-btn pri" disabled={ocupado || motivo.trim().length < 3} onClick={() => accion(() => http.plantillasService.rechazar(campusId, p0!.id, motivo))}>Regresar para corregir</button>
					</div>
				) : (
					<>
						{p0?.puede.archivar && p0.estado !== "archivada" ? <button className="pl-btn tenue" disabled={ocupado} onClick={() => accion(() => http.plantillasService.archivar(campusId, p0.id))}>Archivar</button> : null}
						{p0?.puede.archivar && p0.estado === "archivada" ? <button className="pl-btn" disabled={ocupado} onClick={() => accion(() => http.plantillasService.desarchivar(campusId, p0.id))}>Sacar de Archivadas</button> : null}
						{p0?.puede.version ? <button className="pl-btn" disabled={ocupado} onClick={() => accion(() => http.plantillasService.version(campusId, p0.id), false)}>Hacer versión nueva</button> : null}
						<span className="pl-espacio" />
						{p0?.puede.aprobar ? <button className="pl-btn" disabled={ocupado} onClick={() => setRechazando(true)}>Regresar para corregir</button> : null}
						{editable ? <button className="pl-btn" disabled={ocupado || !nombre.trim()} onClick={() => guardar("borrador")}>Guardar borrador</button> : null}
						{editable && bajaCalidad ? <span className="pl-bloqueo">Calidad {puntos}/10: mejórala para poder mandarla</span> : null}
						{editable && !supervisa ? <button className="pl-btn pri" disabled={ocupado || bajaCalidad || sinNumeros} onClick={() => guardar("sugerir")}>{ocupado ? "Revisando y mandando…" : "Mandar a aprobación"}</button> : null}
						{(editable && supervisa) || (!editable && p0?.puede.aprobar) ? (
							<button className="pl-btn pri" disabled={ocupado || cuentas.length === 0 || (editable && bajaCalidad)}
								onClick={() => editable ? guardar("aprobar") : accion(() => http.plantillasService.aprobar(campusId, p0!.id, cuentas))}>
								{ocupado ? "Mandando a Meta…" : "Aprobar y mandar a Meta (" + cuentas.length + ")"}
							</button>
						) : null}
					</>
				)}
			</div>
		</div>
	);
};

export default PlantillaEditor;
